'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

const faqs = [
  {
    question: 'Is the internship really 100% free with course enrollment?',
    answer: 'Yes! When you enroll in any of our professional certification programs, you get complete access to the internship program at no additional cost. This includes all mentorship, projects, code reviews, and the completion certificate. The standalone internship (without course) costs ₹5,000 + GST.'
  },
  {
    question: 'What kind of projects will I work on?',
    answer: 'You will work on 3 beginner-friendly full-stack projects designed specifically for absolute freshers: a Task Management System (Java Full Stack), a Blog Platform (MEAN Stack), and an E-Commerce Store (MERN Stack). Each project takes 3-4 weeks to complete and involves understanding the codebase, building features, testing, and deployment. You will work with technologies like Java, Spring Boot, Angular, React, Node.js, MongoDB, and MySQL.'
  },
  {
    question: 'How is this different from other internships?',
    answer: 'Unlike traditional internships where you mostly observe or do menial tasks, our program puts you in the driver\'s seat. You will actively code, build features, fix bugs, and deploy to production. You get access to complete codebases, learn architecture patterns, participate in code reviews, and work with industry-standard tools and practices.'
  },
  {
    question: 'Will I get mentorship during the internship?',
    answer: 'Absolutely! With the free internship (course enrollment), you get weekly 1-on-1 mentorship sessions plus live training programs for each project. With the standalone internship, you get twice a week mentorship sessions and live training programs. All interns receive code review feedback, technical guidance, and career advice from experienced developers working in top tech companies.'
  },
  {
    question: 'Do I need prior experience to join?',
    answer: 'Basic programming knowledge is recommended. If you are enrolling with a course, the course will prepare you for the internship. For standalone internship, you should be comfortable with at least one programming language and understand basic web development concepts.'
  },
  {
    question: 'What certificate will I receive?',
    answer: 'You will receive an official Internship Completion Certificate from VitiQuest upon successful completion. The certificate includes details of projects completed, technologies used, and duration. It\'s verified and can be added to your LinkedIn profile and resume.'
  },
  {
    question: 'How long is the internship duration?',
    answer: 'The free internship (with course) offers flexible duration from 2-6 months based on your pace and course program. The standalone internship has a fixed 2-month duration. Both include the same quality of learning and project work.'
  },
  {
    question: 'Will I work on a team or individually?',
    answer: 'You will work individually on projects but collaborate with mentors and other interns. You will participate in code reviews, technical discussions, and learn team workflows. This gives you both independent project ownership and collaborative experience.'
  },
  {
    question: 'What technologies will I learn?',
    answer: 'You will work with modern tech stacks including React, Next.js, Node.js, Python, MongoDB, PostgreSQL, Git, Docker, AWS/cloud platforms, and more. The specific technologies depend on the projects you choose to work on and align with current industry demands.'
  },
  {
    question: 'Can I do this internship while studying/working?',
    answer: 'Yes! The internship is designed to be flexible. You can work at your own pace and schedule mentorship sessions at convenient times. Most interns dedicate 10-15 hours per week. The program adapts to your schedule while ensuring you get complete learning.'
  },
  {
    question: 'Will this help me get a job?',
    answer: 'Definitely! You will build a strong portfolio with real projects, gain hands-on experience with production codebases, and develop job-ready skills. Our interns have successfully landed roles at startups and established companies. The certificate, projects, and skills significantly boost your job prospects.'
  },
  {
    question: 'What happens after the internship?',
    answer: 'After completion, you will have a portfolio of deployed projects, an official certificate, optimized resume and LinkedIn profile, and the skills to crack technical interviews. Course-enrolled students also get additional career support including job referrals and interview preparation.'
  }
];

export default function FAQSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-20 bg-card">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full mb-4">
            <Icon name="QuestionMarkCircleIcon" size={20} variant="solid" />
            <span className="text-sm font-headline font-semibold">Got Questions?</span>
          </div>
          
          <h2 className="text-4xl md:text-5xl font-headline font-bold text-foreground mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            Everything you need to know about our internship program
          </p>
        </div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-surface rounded-xl border border-border overflow-hidden hover:border-primary transition-colors duration-300"
            >
              <button
                onClick={() => toggleFAQ(index)}
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-primary/5 transition-colors duration-300"
              >
                <span className="font-headline font-semibold text-foreground pr-8">
                  {faq.question}
                </span>
                <Icon
                  name={openIndex === index ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                  size={24}
                  variant="outline"
                  className={`text-primary flex-shrink-0 transition-transform duration-300 ${
                    openIndex === index ? 'rotate-180' : ''
                  }`}
                />
              </button>
              
              <div
                className={`overflow-hidden transition-all duration-300 ease-in-out ${
                  openIndex === index ? 'max-h-96' : 'max-h-0'
                }`}
              >
                <div className="px-6 pb-5 pt-2">
                  <p className="text-muted-foreground font-body leading-relaxed">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Still have questions CTA */}
        <div className="mt-12 text-center bg-gradient-to-r from-primary/5 via-accent/5 to-sky-light/5 rounded-xl p-8 border border-primary/20">
          <Icon name="ChatBubbleLeftRightIcon" size={40} variant="outline" className="text-primary mx-auto mb-4" />
          <h3 className="text-xl font-headline font-bold text-foreground mb-2">
            Still have questions?
          </h3>
          <p className="text-muted-foreground mb-4">
            Our team is here to help you understand the program better
          </p>
          <a
            href="mailto:info@vitiquest.com"
            className="inline-flex items-center space-x-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg font-headline font-semibold hover:bg-primary/90 transition-colors duration-300"
          >
            <Icon name="EnvelopeIcon" size={20} variant="solid" />
            <span>Contact Us</span>
          </a>
        </div>
      </div>
    </section>
  );
}
